import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';

const errText = 'font-dm-sans text-sm text-white/55 font-light leading-relaxed';
const hintText = 'font-dm-sans text-[13px] text-white/40 font-light leading-relaxed';

// Phase 1d — partner password reset, screen 2 (set a new password). The user
// lands here from the emailed reset link; supabasePartner (detectSessionInUrl:
// true) establishes a temporary recovery session from the URL token. Uses the
// isolated supabasePartner client only; never the anon customer client.
export default function PartnerResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // 'checking' while we wait for the recovery session to be established from the
  // URL token, then 'valid' or 'invalid'. Direct visits (no token) and expired
  // links resolve to 'invalid'.
  const [sessionState, setSessionState] = useState('checking');

  useEffect(() => {
    let active = true;

    // SECURITY: the gate is "did the user arrive via a genuine password-recovery
    // link?" — NOT "is anyone logged in?". A normal, already-authenticated
    // partner session must NEVER unlock this form, otherwise a logged-in user
    // (or anyone on their machine) could change the password by visiting
    // /partner/reset directly. So we deliberately do not use getSession()/
    // getUser() as the gate. Only two signals count, both tied to a real
    // recovery link processed by detectSessionInUrl:
    //   a) the URL hash carries `type=recovery` (present when landing from the
    //      reset email), and
    //   b) Supabase fires a PASSWORD_RECOVERY auth event.
    const hasRecoveryHash = window.location.hash.includes('type=recovery');

    // Authoritative signal: Supabase processed a recovery link.
    const { data: sub } = supabasePartner.auth.onAuthStateChange((event) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY') setSessionState('valid');
    });

    if (!hasRecoveryHash) {
      // No recovery token in the URL — this is a direct visit. Block it even if a
      // normal session already exists. (Guard against a PASSWORD_RECOVERY event
      // still arriving by only downgrading from 'checking'.)
      setSessionState(prev => (prev === 'checking' ? 'invalid' : prev));
    } else {
      // A recovery hash is present; give detectSessionInUrl a brief moment to
      // process it and fire PASSWORD_RECOVERY. If nothing arrives, treat the
      // link as invalid/expired rather than showing the form.
      setTimeout(() => {
        if (!active) return;
        setSessionState(prev => (prev === 'checking' ? 'invalid' : prev));
      }, 1500);
    }

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const handleSubmit = async () => {
    const next = {};
    if (password.length < 6) next.password = 'Password must be at least 6 characters';
    if (confirmPassword !== password) next.confirmPassword = 'Passwords do not match';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const { error } = await supabasePartner.auth.updateUser({ password });
      if (error) {
        // No valid recovery session (expired/invalid link, or direct visit) —
        // updateUser fails because there's no authenticated user to update.
        setSessionState('invalid');
        return;
      }
      setDone(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Portal
        </p>

        {done ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Password updated
            </h1>
            <p className="font-dm-sans text-sm text-white/70 font-light mt-3 leading-relaxed">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link
              to="/partner/login"
              className="block w-full text-center mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
            >
              Sign in
            </Link>
          </>
        ) : sessionState === 'invalid' ? (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Link expired
            </h1>
            <p className="font-dm-sans text-sm text-white/70 font-light mt-3 leading-relaxed">
              This reset link is invalid or has expired. Please request a new one.
            </p>
            <Link
              to="/partner/forgot"
              className="block w-full text-center mt-8 py-[14px] border border-[rgba(212,168,67,0.4)] text-[#D4A843] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:border-[rgba(212,168,67,0.7)] hover:bg-[rgba(212,168,67,0.06)] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
            >
              Request a new link
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
              Set a new password
            </h1>
            <p className="font-dm-sans text-sm text-white/55 font-light mb-7 leading-relaxed">
              Choose a new password for your partner account.
            </p>

            <div className="flex flex-col gap-5">

              <div className="group flex flex-col gap-2">
                <label className={labelBase}>New Password</label>
                <div className={borderBase}>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    className={inputBase}
                    autoComplete="new-password"
                  />
                </div>
                {errors.password ? (
                  <p className={errText}>{errors.password}</p>
                ) : (
                  <p className={hintText}>At least 6 characters.</p>
                )}
              </div>

              <div className="group flex flex-col gap-2">
                <label className={labelBase}>Confirm Password</label>
                <div className={borderBase}>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    className={inputBase}
                    autoComplete="new-password"
                  />
                </div>
                {errors.confirmPassword ? (
                  <p className={errText}>{errors.confirmPassword}</p>
                ) : (
                  <p className={hintText}>Re-enter your new password</p>
                )}
              </div>

            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || sessionState === 'checking'}
              className="w-full mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>

            {submitError && (
              <p className="font-dm-sans text-center text-sm text-white/55 mt-3.5 leading-relaxed">
                {submitError}
              </p>
            )}
          </>
        )}

        <div className="mt-6 pt-4 border-t border-white/[0.07] text-center">
          <Link
            to="/"
            className="font-dm-sans text-[13px] text-white/40 hover:text-[rgba(201,168,76,0.85)] transition-colors duration-300"
          >
            ← Back to main site
          </Link>
        </div>

      </div>
    </div>
  );
}
